import { useState, type ReactNode } from 'react';
import { errorMessage } from '../api/client';
import type {
  ContractCorrectableField,
  ContractFlag,
  ContractRegister,
  ContractRegisterEntry,
  CorrectContractRequest,
} from '../api/types';
import {
  CONTRACT_FLAG_LABELS,
  CONTRACT_STATUS_LABELS,
  CONTRACT_TYPE_LABELS,
  noticePeriodLabel,
} from '../lib/contracts';
import { formatDateOnly } from '../lib/date-only';
import { Button } from './button';
import { Dialog } from './dialog';
import { Input } from './input';
import { Chip, type ChipTone } from './status-chip';
import type { IconName } from './icons';

/**
 * The CONTRACT REGISTER (ADR-0054), one component for every portal: the room's
 * contracts sorted by expiration — document, type, counterparty, executed (the
 * date the last party signed), effective, expires, notice by, auto-renew and
 * flags — then how old the latest financial
 * statements are. Each date is the one the document states (selected by the AI
 * from the dates the text contains, never generated); "Not found" when nothing was
 * confident. Flags are date comparisons, never an opinion.
 *
 * The server builds the register from the caller's own visible documents, so the
 * same component serves the admin, the seller and a buyer. `onCorrect` (admin and
 * bound seller) adds an Edit action; `rowAction` adds a per-row control (the
 * seller's "Upload a current copy").
 */

const FLAG_CHIP: Record<ContractFlag, { tone: ChipTone; icon: IconName }> = {
  expired: { tone: 'error', icon: 'alert' },
  expires_within_90_days: { tone: 'check', icon: 'calendar' },
  expires_before_closing: { tone: 'loi', icon: 'calendar' },
};

export function ContractFlagChip({ flag }: { flag: ContractFlag }) {
  const spec = FLAG_CHIP[flag];
  return (
    <Chip tone={spec.tone} icon={spec.icon}>
      {CONTRACT_FLAG_LABELS[flag]}
    </Chip>
  );
}

const fieldClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

function NotFound() {
  return <span className="text-ink-3">Not found</span>;
}

/** A value, marked when a person set it. */
function Field({
  entry,
  field,
  children,
}: {
  entry: ContractRegisterEntry;
  field: ContractCorrectableField;
  children: ReactNode;
}) {
  const corrected = entry.corrected.includes(field);
  return (
    <span className="whitespace-nowrap">
      {children}
      {corrected ? (
        <span className="ml-1 text-xs text-ink-3" title="Set by a person">
          (edited)
        </span>
      ) : null}
    </span>
  );
}

function dateCell(
  entry: ContractRegisterEntry,
  field: ContractCorrectableField,
  value: string | null,
) {
  return (
    <Field entry={entry} field={field}>
      {value ? formatDateOnly(value) : <NotFound />}
    </Field>
  );
}

function autoRenewText(value: boolean | null): ReactNode {
  if (value === null) return <span className="text-ink-3">Not stated</span>;
  return value ? 'Yes' : 'No';
}

export interface ContractRegisterViewProps {
  register: ContractRegister;
  /** Admin / bound seller: an Edit action per row; resolves when saved, throws on failure. */
  onCorrect?: (documentId: string, patch: CorrectContractRequest) => Promise<void>;
  /** An extra control per row (the seller's "Upload a current copy" on an expired contract). */
  rowAction?: (entry: ContractRegisterEntry) => ReactNode;
  /** Shown when the caller can see no contracts. */
  emptyText?: string;
  /** A "?" help tip shown at the end of the intro line, which explains the flags. */
  introAside?: ReactNode;
}

export function ContractRegisterView({
  register,
  onCorrect,
  rowAction,
  emptyText = 'No contracts have been filed in this room yet.',
  introAside,
}: ContractRegisterViewProps) {
  const [editing, setEditing] = useState<ContractRegisterEntry | null>(null);
  const contracts = register.contracts;
  const stale = register.financialStatements;
  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Dates as each document states them, read by AI from the document’s own text — “Not found”
        when it could not tell. Flags compare those dates with today
        {register.expectedClosingDate
          ? ` and the expected closing date (${formatDateOnly(register.expectedClosingDate)})`
          : ''}
        .{onCorrect ? ' Correct any date that is wrong; your change is kept.' : ''}
        {introAside ? <span className="ml-1.5 inline-flex align-middle">{introAside}</span> : null}
      </p>
      {contracts.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-left text-sm" aria-label="Contract register">
            <thead className="bg-surface-2 text-xs uppercase tracking-wide text-ink-2">
              <tr>
                <th scope="col" className="px-3 py-2">
                  Document
                </th>
                <th scope="col" className="px-3 py-2">
                  Type
                </th>
                <th scope="col" className="px-3 py-2">
                  Counterparty
                </th>
                <th scope="col" className="px-3 py-2">
                  Executed
                </th>
                <th scope="col" className="px-3 py-2">
                  Effective
                </th>
                <th scope="col" className="px-3 py-2">
                  Expires
                </th>
                <th scope="col" className="px-3 py-2">
                  Notice by
                </th>
                <th scope="col" className="px-3 py-2">
                  Auto-renew
                </th>
                <th scope="col" className="px-3 py-2">
                  Flag
                </th>
                {onCorrect || rowAction ? (
                  <th scope="col" className="px-3 py-2">
                    <span className="sr-only">Actions</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody className="divide-y">
              {contracts.map((entry) => (
                <ContractRow
                  key={entry.documentId}
                  entry={entry}
                  onEdit={onCorrect ? () => setEditing(entry) : undefined}
                  action={rowAction?.(entry)}
                  hasActions={Boolean(onCorrect || rowAction)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {stale.length > 0 ? (
        <section aria-label="Latest financial statements" className="space-y-2">
          <h3 className="text-sm font-semibold">Latest financial statements</h3>
          <ul className="divide-y rounded-md border">
            {stale.map((s) => (
              <li
                key={s.kind}
                data-statement-kind={s.kind}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
              >
                <span>
                  <span className="font-medium">{s.kind}</span>: data as of{' '}
                  {formatDateOnly(s.latestDate)} ({s.daysOld} {s.daysOld === 1 ? 'day' : 'days'}{' '}
                  ago)
                </span>
                {s.stale ? (
                  <Chip tone="check" icon="alert">
                    Older than 90 days
                  </Chip>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {editing && onCorrect ? (
        <CorrectContractDialog
          entry={editing}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            await onCorrect(editing.documentId, patch);
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}

function ContractRow({
  entry,
  onEdit,
  action,
  hasActions,
}: {
  entry: ContractRegisterEntry;
  onEdit?: () => void;
  action?: ReactNode;
  hasActions: boolean;
}) {
  const read = entry.status === 'extracted';
  const notice = noticePeriodLabel(entry.noticePeriod);
  return (
    <tr data-contract-id={entry.documentId} className="align-top">
      <td className="max-w-[16rem] px-3 py-2 font-medium break-words">
        {entry.name || 'Untitled document'}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">{CONTRACT_TYPE_LABELS[entry.contractType]}</td>
      {read ? (
        <>
          <td className="max-w-[12rem] px-3 py-2 break-words">
            <Field entry={entry} field="counterparty">
              {entry.counterparty ?? <NotFound />}
            </Field>
          </td>
          <td className="px-3 py-2 whitespace-nowrap">
            {entry.executionDate ? formatDateOnly(entry.executionDate) : <NotFound />}
          </td>
          <td className="px-3 py-2">
            {dateCell(entry, 'effective_date', entry.effectiveDate)}
            {entry.effectiveFromExecution && entry.effectiveDate ? (
              <span className="block text-xs text-ink-3">On signing</span>
            ) : null}
          </td>
          <td className="px-3 py-2">{dateCell(entry, 'expiration_date', entry.expirationDate)}</td>
          <td className="px-3 py-2">
            {dateCell(entry, 'notice_by', entry.noticeBy)}
            {notice && entry.noticeBy ? (
              <span className="block text-xs text-ink-3">{notice} notice</span>
            ) : null}
          </td>
          <td className="px-3 py-2">
            <Field entry={entry} field="auto_renew">
              {autoRenewText(entry.autoRenew)}
            </Field>
          </td>
          <td className="px-3 py-2">
            <div className="flex flex-wrap gap-1">
              {entry.flags.map((f) => (
                <ContractFlagChip key={f} flag={f} />
              ))}
            </div>
          </td>
        </>
      ) : (
        <td colSpan={7} className="px-3 py-2">
          <Chip tone={entry.status === 'pending' ? 'processing' : 'neutral'}>
            {CONTRACT_STATUS_LABELS[entry.status as Exclude<typeof entry.status, 'extracted'>]}
          </Chip>
        </td>
      )}
      {hasActions ? (
        <td className="px-3 py-2">
          <div className="flex flex-col items-start gap-1">
            {onEdit ? (
              <Button
                variant="outline"
                size="sm"
                onClick={onEdit}
                aria-label={`Correct the dates of ${entry.name || 'this document'}`}
              >
                Edit
              </Button>
            ) : null}
            {action}
          </div>
        </td>
      ) : null}
    </tr>
  );
}

/** The correction form: only the fields a person changes are sent. */
export function CorrectContractDialog({
  entry,
  onClose,
  onSave,
}: {
  entry: ContractRegisterEntry;
  onClose: () => void;
  onSave: (patch: CorrectContractRequest) => Promise<void>;
}) {
  const [effective, setEffective] = useState(entry.effectiveDate ?? '');
  const [expires, setExpires] = useState(entry.expirationDate ?? '');
  const [noticeBy, setNoticeBy] = useState(entry.noticeBy ?? '');
  const [autoRenew, setAutoRenew] = useState(
    entry.autoRenew === null ? '' : entry.autoRenew ? 'yes' : 'no',
  );
  const [counterparty, setCounterparty] = useState(entry.counterparty ?? '');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orNull = (v: string) => (v.trim() === '' ? null : v.trim());
  const patch: CorrectContractRequest = {
    ...(orNull(effective) !== entry.effectiveDate ? { effectiveDate: orNull(effective) } : {}),
    ...(orNull(expires) !== entry.expirationDate ? { expirationDate: orNull(expires) } : {}),
    ...(orNull(noticeBy) !== entry.noticeBy ? { noticeBy: orNull(noticeBy) } : {}),
    ...((autoRenew === '' ? null : autoRenew === 'yes') !== entry.autoRenew
      ? { autoRenew: autoRenew === '' ? null : autoRenew === 'yes' }
      : {}),
    ...(orNull(counterparty) !== entry.counterparty ? { counterparty: orNull(counterparty) } : {}),
  };
  const canSave = Object.keys(patch).length > 0 && !pending;

  async function save() {
    setPending(true);
    setError(null);
    try {
      await onSave(patch);
    } catch (err) {
      setError(errorMessage(err) ?? 'Could not save. Please try again.');
      setPending(false);
    }
  }

  const id = (name: string) => `contract-${entry.documentId}-${name}`;
  return (
    <Dialog
      title="Correct contract dates"
      description={entry.name || 'Untitled document'}
      onClose={onClose}
      busy={pending}
      footer={
        <>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button size="sm" disabled={!canSave} onClick={() => void save()}>
              {pending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {(
          [
            ['effective', 'Effective', effective, setEffective],
            ['expires', 'Expires', expires, setExpires],
            ['notice', 'Notice by', noticeBy, setNoticeBy],
          ] as const
        ).map(([key, label, value, set]) => (
          <div key={key} className="space-y-1.5">
            <label htmlFor={id(key)} className="text-sm font-medium">
              {label}
            </label>
            <input
              id={id(key)}
              type="date"
              className={fieldClass}
              value={value}
              onChange={(e) => set(e.target.value)}
              disabled={pending}
            />
          </div>
        ))}
        <div className="space-y-1.5">
          <label htmlFor={id('renew')} className="text-sm font-medium">
            Auto-renew
          </label>
          <select
            id={id('renew')}
            className={fieldClass}
            value={autoRenew}
            onChange={(e) => setAutoRenew(e.target.value)}
            disabled={pending}
          >
            <option value="">Not stated</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <label htmlFor={id('party')} className="text-sm font-medium">
            Counterparty
          </label>
          <Input
            id={id('party')}
            value={counterparty}
            maxLength={200}
            onChange={(e) => setCounterparty(e.target.value)}
            disabled={pending}
          />
        </div>
      </div>
    </Dialog>
  );
}
